import { useOptimisticMutation } from "../hooks/useOptimisticMutation";
import { encodeFeedKey } from "@/utils/sportFeedKey";
import { applyOptimisticFeedWrites } from "@/utils/optimisticFeedSuspensions";
import { upsertLeaguesSuspensions } from "@/api/leaguesNew";
import { LeaguesNewQueryKeys } from "@/tanstackQuery/queries/leaguesNew";
import type {
  UpsertLeagueSuspensionsParams,
  LeagueSuspensionsEditorResponse,
} from "@/types/ApiResponses/League";

export const useUpsertLeaguesSuspensions = (targetUsername?: string) => {
  const queryKey = [LeaguesNewQueryKeys.LeaguesSuspensions, targetUsername];

  return useOptimisticMutation<
    UpsertLeagueSuspensionsParams,
    LeagueSuspensionsEditorResponse
  >({
    queryKey,
    mutationFn: upsertLeaguesSuspensions,
    optimisticUpdate: (variable, oldData) => {
      if (!oldData) return oldData;

      const newSuspensions = { ...oldData.suspensions };
      for (const s of variable.suspensions ?? []) {
        newSuspensions[s.leagueId] = { id: s.leagueId, active: s.active };
      }

      const newFeedSuspensions = applyOptimisticFeedWrites(
        oldData.feedSuspensions,
        (variable.feedSuspensions ?? []).map((s) => ({
          key: encodeFeedKey(s.leagueId, s.feedType),
          active: s.active,
        })),
        (variable.feedOverrides ?? []).map((o) => ({
          key: encodeFeedKey(o.leagueId, o.feedType),
          open: o.open,
        }))
      );

      return {
        ...oldData,
        suspensions: newSuspensions,
        feedSuspensions: newFeedSuspensions,
      };
    },
    // Prefix match: the editor query is also keyed by the sport scope
    // ([key, target, sportId]), so an exact [key, target] key would never
    // reach it and a switch would only move after the socket refetch.
    updateOptions: { exactQueryKey: false },
  });
};
